/** @file Factory for `<domain>.set<Field>` commands: resolve target, validate, assign, stamp updatedAt. */
import { z } from 'zod';
import type { EntityKind } from '@pose2d/format/types';
import { defineCommand, type CommandDef } from '../defineCommand';
import { entityDraft } from './entityDraft';

const isPlainObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Builds a setter command; `schema` validates the new value. */
export function defineSetter(spec: { domain: string; kind: EntityKind; field: string; schema: z.ZodType; description: string }): CommandDef<{ target: string; value: unknown }> {
  const path = spec.field.split('.');
  const last = path.pop() ?? spec.field;
  return defineCommand({
    name: `${spec.domain}.set${last.charAt(0).toUpperCase()}${last.slice(1)}`,
    description: spec.description,
    input: z.object({ target: z.string().min(1), value: spec.schema }).strict(),
    apply(draft, input, ctx) {
      let holder = entityDraft(draft, spec.kind, ctx.resolve(spec.kind, input.target));
      for (const key of path) holder = (holder[key] ??= {}) as Record<string, unknown>;
      const current = holder[last];
      if (isPlainObject(current) && isPlainObject(input.value)) Object.assign(current, input.value);
      else holder[last] = input.value;
      draft.meta.updatedAt = ctx.now;
    },
  });
}
